/*!
 * Created by netlogix GmbH & Co. KG.
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AppointmentService } from '@appointments/services/appointment.service';
import * as AppointmentActions from '@appointments/store/appointment.actions';
import { State } from '@core/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Appointment } from '@shared/models/appointment';
import { getCurrentDate } from '@tours/store/tours.selectors';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class AppointmentEffects {
  constructor(
    private actions$: Actions<AppointmentActions.AppointmentActions>,
    private appointmentService: AppointmentService,
    private store: Store<State>,
    private translateService: TranslateService,
    private snackBarService: MatSnackBar,
    private router: Router
  ) { }

  
  loadAppointments$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.LOAD_APPOINTMENTS),
    withLatestFrom(this.store.select(getCurrentDate)),
    switchMap(([_, date]) => this.appointmentService.getAppointmentsForDay(date)),
    map(appointments => new AppointmentActions.LoadAppointmentsSuccess(appointments)),
    catchError(() => of(new AppointmentActions.LoadAppointmentsFailure()))
  ));

  
  loadAppointmentsFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.LOAD_APPOINTMENTS_FAILURE),
    switchMap(() => this.translateService.get('appointment.errors.load').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  loadAppointment$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.LOAD_APPOINTMENT),
    map(action => action.appointmentId),
    switchMap(id => {
      if (id) {
        return this.appointmentService.getAppointment(id);
      }
      return of({ } as Appointment);
    }),
    map(appointment => new AppointmentActions.LoadAppointmentSuccess(appointment)),
    catchError(() => of(new AppointmentActions.LoadAppointmentFailure()))
  ));

  
  loadAppointmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.LOAD_APPOINTMENT_FAILURE),
    switchMap(() => this.translateService.get('appointment.errors.load').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  persistAppointment$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.PERSIST_APPOINTMENT),
    map(action => action.appointment),
    switchMap(appointment => {
      if (appointment['@id']) {
        return this.appointmentService.updateAppointment(appointment);
      }
      return this.appointmentService.createAppointment(appointment);
    }),
    map(appointment => new AppointmentActions.PersistAppointmentSuccess(appointment)),
    catchError(() => of(new AppointmentActions.PersistAppointmentFailure()))
  ));

  
  persistAppointmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.PERSIST_APPOINTMENT_FAILURE),
    switchMap(() => this.translateService.get('appointment.errors.persist').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  persistAppointmentSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.PERSIST_APPOINTMENT_SUCCESS),
    tap(() => this.router.navigate(['tours']))
  ), { dispatch: false });

  
  deleteAppointment$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.DELETE_APPOINTMENT),
    map(action => action.appointmentId),
    switchMap(id => this.appointmentService.deleteAppointment(id).pipe(
      switchMap(() => [
        new AppointmentActions.DeleteAppointmentSuccess(),
        new AppointmentActions.LoadAppointments()
      ]),
      catchError(() => of(new AppointmentActions.DeleteAppointmentFailure()))
    ))
  ));

  
  deleteAppointmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AppointmentActions.DELETE_APPOINTMENT_FAILURE),
    switchMap(() => this.translateService.get('appointment.errors.delete').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });
}
